import { enrollMfaPath } from './api-client';
import { featureQueryKey } from './api-client';
import { AssignWindowLocation } from './redirect-allowlist';
import { ValidateContinueUrl } from './redirect-allowlist';
import { organizationApi } from '../organization-api';
import { preferencesApi } from '../preferences-api';
import { userApi } from '../user-api';

import type { NavigateFunction } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';

const shellQueryStaleTimeMs = 30000;

let landingInFlight: Promise<boolean> | null = null;

// One event can trip two callers — enabling a second factor both fires onEnabled and flips the
// page into its cleared-landing. A second run would remove the first run's in-flight queries, and
// a cancelled query reads here as a dead session, which sends that caller to the login screen.
export async function completeAuthenticatedLanding(options: {
  queryClient: QueryClient;
  navigate: NavigateFunction;
  continueUrl?: string | null;
}): Promise<boolean> {
  if (landingInFlight !== null) {
    return landingInFlight;
  }
  landingInFlight = RunAuthenticatedLanding(options);
  try {
    return await landingInFlight;
  } finally {
    landingInFlight = null;
  }
}

async function RunAuthenticatedLanding(options: {
  queryClient: QueryClient;
  navigate: NavigateFunction;
  continueUrl?: string | null;
}): Promise<boolean> {
  let landed = false;
  const userQueryKey = featureQueryKey('user');
  options.queryClient.removeQueries({ queryKey: userQueryKey });
  options.queryClient.removeQueries({ queryKey: featureQueryKey('organization') });
  options.queryClient.removeQueries({ queryKey: featureQueryKey('preferences') });

  let loginPath = '/login';
  if (typeof options.continueUrl === 'string' && options.continueUrl.length > 0) {
    loginPath = `/login?continue=${encodeURIComponent(options.continueUrl)}`;
  }

  try {
    const response = await options.queryClient.fetchQuery({
      queryKey: userQueryKey,
      queryFn: async () => {
        const result = await userApi.getUser.query({});
        return result;
      },
      staleTime: shellQueryStaleTimeMs,
    });
    if (response.status === 200 && response.body.mfaEnrollmentRequired) {
      let enrollPath = enrollMfaPath;
      if (typeof options.continueUrl === 'string' && options.continueUrl.length > 0) {
        enrollPath = `${enrollMfaPath}?continue=${encodeURIComponent(options.continueUrl)}`;
      }
      options.navigate(enrollPath, { replace: true });
      landed = true;
    } else if (response.status === 200) {
      const organizationKey = featureQueryKey('organization', 'active');
      const organizationResponse = await options.queryClient.fetchQuery({
        queryKey: organizationKey,
        queryFn: async () => {
          const result = await organizationApi.getOrganization.query({ query: {} });
          return result;
        },
        staleTime: shellQueryStaleTimeMs,
      });
      await options.queryClient.prefetchQuery({
        queryKey: featureQueryKey('preferences'),
        queryFn: async () => {
          const result = await preferencesApi.getPreferences.query({});
          return result;
        },
        staleTime: shellQueryStaleTimeMs,
      });
      let hasOrganization = false;
      if (
        organizationResponse.status === 200 &&
        organizationResponse.body &&
        typeof organizationResponse.body.name === 'string'
      ) {
        hasOrganization = true;
      }
      if (!hasOrganization) {
        options.navigate('/auth/no-organizations', { replace: true });
        landed = true;
      } else {
        const validated = ValidateContinueUrl(options.continueUrl);
        if (validated && validated.startsWith('/')) {
          options.navigate(validated, { replace: true });
          landed = true;
        } else if (validated) {
          AssignWindowLocation(validated);
          landed = true;
        } else {
          options.navigate('/dashboard', { replace: true });
          landed = true;
        }
      }
    } else {
      options.navigate(loginPath, { replace: true });
    }
  } catch {
    options.navigate(loginPath, { replace: true });
  }
  return landed;
}
